<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import wootConstants from 'dashboard/constants/globals';

import FormSelect from 'v3/components/Form/Select.vue';

const { t } = useI18n();
const { updateUISettings, uiSettings } = useUISettings();
const getters = useStoreGetters();

const currentUser = computed(() => getters.getCurrentUser.value);
const currentAccountId = computed(() => getters.getCurrentAccountId.value);

// Offer only the tabs this agent is actually allowed to see. Without this the
// dropdown would happily store `all` for a custom role that cannot use it, the
// toast would say saved, and every ChatList mount would silently fall back to
// `me` -- the one screen meant to tell the agent what will happen would lie.
const assigneeTabOptions = computed(() => {
  const permissions = getUserPermissions(
    currentUser.value,
    currentAccountId.value
  );
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    permissions,
    item => item.permissions
  ).map(({ key }) => ({
    value: key,
    // Reuse the tab strip's own strings so the two surfaces cannot drift.
    label: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
  }));
});

// CHAT_STATUS_FILTER_ITEMS values are objects with a TEXT key, unlike
// ASSIGNEE_TYPE_TABS which are flat strings. Without .TEXT this renders the
// raw i18n key on screen.
const statusOptions = computed(() =>
  Object.values(wootConstants.STATUS_TYPE).map(value => ({
    value,
    label: t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${value}.TEXT`),
  }))
);

const selectedTab = computed({
  get: () =>
    uiSettings.value?.default_assignee_tab ?? wootConstants.ASSIGNEE_TYPE.ME,
  set: value => updateUISettings({ default_assignee_tab: value }),
});

const selectedStatus = computed({
  get: () =>
    uiSettings.value?.default_conversation_status ??
    wootConstants.STATUS_TYPE.OPEN,
  set: value => updateUISettings({ default_conversation_status: value }),
});
</script>

<template>
  <div class="flex flex-col gap-6 items-start w-full">
    <div class="flex gap-2 justify-between w-full items-start">
      <div>
        <label class="text-n-gray-12 font-medium leading-6 text-sm">
          {{
            $t('PROFILE_SETTINGS.FORM.OPENING_VIEW_SECTION.ASSIGNEE_TAB.TITLE')
          }}
        </label>
        <p class="text-n-gray-11">
          {{
            $t('PROFILE_SETTINGS.FORM.OPENING_VIEW_SECTION.ASSIGNEE_TAB.NOTE')
          }}
        </p>
      </div>
      <FormSelect
        v-model="selectedTab"
        name="defaultAssigneeTab"
        class="min-w-28 mt-px"
        :value="selectedTab"
        :options="assigneeTabOptions"
        label=""
      >
        <option
          v-for="option in assigneeTabOptions"
          :key="option.value"
          :value="option.value"
          :selected="option.value === selectedTab"
        >
          {{ option.label }}
        </option>
      </FormSelect>
    </div>

    <div class="flex gap-2 justify-between w-full items-start">
      <div>
        <label class="text-n-gray-12 font-medium leading-6 text-sm">
          {{ $t('PROFILE_SETTINGS.FORM.OPENING_VIEW_SECTION.STATUS.TITLE') }}
        </label>
        <p class="text-n-gray-11">
          {{ $t('PROFILE_SETTINGS.FORM.OPENING_VIEW_SECTION.STATUS.NOTE') }}
        </p>
      </div>
      <FormSelect
        v-model="selectedStatus"
        name="defaultConversationStatus"
        class="min-w-28 mt-px"
        :value="selectedStatus"
        :options="statusOptions"
        label=""
      >
        <option
          v-for="option in statusOptions"
          :key="option.value"
          :value="option.value"
          :selected="option.value === selectedStatus"
        >
          {{ option.label }}
        </option>
      </FormSelect>
    </div>
  </div>
</template>
